/**
 * @copyright 2024-2026 David Baugh
 * @license Proprietary - All Rights Reserved
 * Unauthorized copying, modification, or distribution is strictly prohibited.
 */

import React from 'react';
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { useBranding } from "../contexts/BrandingContext";

const DemoBanner = () => (
  <div className="w-full max-w-4xl mb-4 px-4 py-3 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-sm text-center">
    <strong>This is a public demo.</strong> The database resets nightly — feel free to break things,
    create users, edit tests, anything. Default credentials come back every morning.
  </div>
);

const DemoCredentialsCard = ({ credentials }) => (
  <Card className="bg-blue-50 border-blue-200 mb-6">
    <CardHeader>
      <CardTitle className="text-lg">Demo credentials</CardTitle>
      <CardDescription>
        Three accounts are pre-seeded so you can explore each role without signing up.
      </CardDescription>
    </CardHeader>
    <CardContent>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
        <div className="p-3 bg-white rounded border border-blue-200">
          <div className="font-semibold mb-1">Student</div>
          <div className="text-xs text-gray-600 mb-1">use the Login button</div>
          <div className="font-mono text-xs">
            <div>student ID: <span className="select-all">{credentials.student.studentId}</span></div>
            <div>PIN: <span className="select-all">{credentials.student.pin}</span></div>
          </div>
        </div>
        <div className="p-3 bg-white rounded border border-blue-200">
          <div className="font-semibold mb-1">Teacher</div>
          <div className="text-xs text-gray-600 mb-1">use &ldquo;Staff Login&rdquo;</div>
          <div className="font-mono text-xs">
            <div>email: <span className="select-all">{credentials.teacher.email}</span></div>
            <div>password: <span className="select-all">{credentials.teacher.password}</span></div>
          </div>
        </div>
        <div className="p-3 bg-white rounded border border-blue-200">
          <div className="font-semibold mb-1">Admin</div>
          <div className="text-xs text-gray-600 mb-1">use &ldquo;Staff Login&rdquo;</div>
          <div className="font-mono text-xs">
            <div>email: <span className="select-all">{credentials.admin.email}</span></div>
            <div>password: <span className="select-all">{credentials.admin.password}</span></div>
          </div>
        </div>
      </div>
      <p className="text-xs text-blue-800 mt-3">
        New here? Start with the student account and take the &ldquo;Welcome — Platform Tour&rdquo; test.
        The student login form has a &ldquo;Staff Login →&rdquo; link to switch over.
      </p>
    </CardContent>
  </Card>
);

const LandingPage = ({ onLoginClick, onRegisterClick }) => {
  const { branding, demo } = useBranding();
  const isDemo = !!demo?.isDemoInstance;

  return (
    <div
      className="min-h-screen flex flex-col justify-center items-center p-4"
      style={{
        backgroundColor: 'var(--color-background)',
        color: 'var(--color-text)'
      }}
    >
      {isDemo && <DemoBanner />}
      <Card className="w-full max-w-4xl bg-white shadow-xl">
        <CardHeader className="text-center">
          {branding.logo && (
            <div className="flex justify-center mb-4">
              <img
                src={branding.logo}
                alt={`${branding.schoolName} logo`}
                className="h-24 w-24 object-contain"
              />
            </div>
          )}
          <CardTitle className="text-3xl font-bold" style={{ color: 'var(--color-primary)' }}>
            {branding.schoolName}
          </CardTitle>
          <CardDescription>Empowering safe and skilled practices</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center mb-8">
            <h2 className="text-xl font-semibold mb-2">Welcome to the Safety Hub</h2>
            <p className="mb-4">Enhance your skills, stay safe, and track your progress in woodworking.</p>
            <div className="flex justify-center gap-4">
              <Button onClick={onLoginClick} className="bg-blue-500 hover:bg-blue-600">Login</Button>
              {!isDemo && (
                <Button onClick={onRegisterClick} variant="outline">Register</Button>
              )}
            </div>
          </div>
          {isDemo && demo.credentials && (
            <DemoCredentialsCard credentials={demo.credentials} />
          )}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { title: "Safety First", description: "Access comprehensive safety guides for all tools" },
              { title: "Skill Tracking", description: "Monitor your progress" },
              { title: "Interactive Learning", description: "Engage with quizzes, review incorrect questions and more..." }
            ].map((feature, index) => (
              <Card key={index} className="bg-gray-50">
                <CardHeader>
                  <CardTitle className="text-lg">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p>{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>
      <footer className="mt-8 text-center text-gray-600">
        <div className="flex flex-col gap-2">
          <p className="font-semibold">{branding.schoolName}</p>
          {branding.schoolAddress && <p className="text-sm">{branding.schoolAddress}</p>}
          <div className="flex justify-center gap-4 text-sm">
            {branding.contactEmail && (
              <a href={`mailto:${branding.contactEmail}`} className="hover:underline" style={{ color: 'var(--color-primary)' }}>
                {branding.contactEmail}
              </a>
            )}
            {branding.contactPhone && <span>{branding.contactPhone}</span>}
          </div>
          {isDemo && (
            <p className="text-xs">
              <a href="/demo-terms" className="underline hover:text-gray-800">Demo terms of use</a>
            </p>
          )}
          <p className="text-xs text-gray-400 mt-2">© 2024-{new Date().getFullYear()} David Baugh / @baughtnet IT Solutions. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
